import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserCog, Plus, Trash2, Pencil, KeyRound } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const ROLE_LABEL = { super_admin: "Super Admin", team_member: "Team / VA", client_admin: "Client Admin" };
const EMPTY = { email: "", name: "", password: "", role: "team_member", assigned_client_ids: [] };

export default function UsersPage() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => { toast.success("User deleted"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const sendLogin = useMutation({
    mutationFn: async (id) => (await api.post(`/users/${id}/send-login`)).data,
    onSuccess: (d) => toast.success(`Login details emailed to ${d.email}`),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const clientName = (id) => clients.find((c) => c.id === id)?.business_name || id;

  return (
    <div>
      <PageHeader title="Users" subtitle="Manage VSM team & client access">
        <UserDialog clients={clients} onDone={refresh} />
      </PageHeader>

      {isLoading ? <Spinner /> : users.length === 0 ? <EmptyState icon={UserCog} title="No users" /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Client Access</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-semibold text-slate-900">{u.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                  <TableCell><Badge variant="outline">{ROLE_LABEL[u.role] || u.role}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600">{u.role === "super_admin" ? "All clients" : (u.assigned_client_ids || []).map(clientName).join(", ") || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`send-login-${u.id}`} title="Email new login details"><KeyRound className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Email new login details to {u.name}?</AlertDialogTitle><AlertDialogDescription>A new temporary password will be generated and emailed to {u.email}. Their current password will stop working, and they'll be asked to set a new password on first sign-in.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => sendLogin.mutate(u.id)} data-testid={`confirm-send-login-${u.id}`}>Send Email</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <UserDialog clients={clients} userObj={u} onDone={refresh} trigger={<Button variant="ghost" size="sm" data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>} />
                    {u.id !== me.id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-rose-600" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {u.name}?</AlertDialogTitle><AlertDialogDescription>This user will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-600" onClick={() => del.mutate(u.id)} data-testid="confirm-delete-user">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function UserDialog({ clients, userObj, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(userObj ? { ...userObj, password: "" } : EMPTY);
  const isEdit = !!userObj;
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleClient = (id) => setF((s) => ({ ...s, assigned_client_ids: s.assigned_client_ids.includes(id) ? s.assigned_client_ids.filter((x) => x !== id) : [...s.assigned_client_ids, id] }));

  const mut = useMutation({
    mutationFn: async () => {
      const body = { email: f.email, name: f.name, role: f.role, assigned_client_ids: f.role === "super_admin" ? [] : f.assigned_client_ids };
      if (f.password) body.password = f.password;
      return isEdit ? (await api.put(`/users/${userObj.id}`, body)).data : (await api.post("/users", body)).data;
    },
    onSuccess: () => { toast.success(isEdit ? "User updated" : "User created"); setOpen(false); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(userObj ? { ...userObj, password: "" } : EMPTY); }}>
      <DialogTrigger asChild>{trigger || <Button data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1.5" /> Add User</Button>}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{isEdit ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => upd("name", e.target.value)} className="mt-1" data-testid="user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} className="mt-1" data-testid="user-email" /></div>
          <div><Label>{isEdit ? "New Password (optional)" : "Password"}</Label><Input type="password" value={f.password} onChange={(e) => upd("password", e.target.value)} className="mt-1" data-testid="user-password" /></div>
          <div>
            <Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => upd("role", v)}>
              <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="team_member">Team Member / VA</SelectItem>
                <SelectItem value="client_admin">Client Admin (read-only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.role !== "super_admin" && (
            <div>
              <Label>Client Access</Label>
              <div className="mt-1.5 space-y-1.5 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                {clients.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`user-client-${c.id}`}>
                    <Checkbox checked={f.assigned_client_ids.includes(c.id)} onCheckedChange={() => toggleClient(c.id)} />
                    {c.business_name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={!f.name || !f.email || mut.isPending} data-testid="save-user-btn">{isEdit ? "Save" : "Create User"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
